import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { ReactNode } from 'react';
import { T } from '~/components/I18n/Translation';
import lv1 from '~/components/Icon/Citizen/lv1.png';
import lv2 from '~/components/Icon/Citizen/lv2.png';
import lv3 from '~/components/Icon/Citizen/lv3.png';
import lv4 from '~/components/Icon/Citizen/lv4.png';
import avatar1 from '~/components/Level/avatar1.png';
import avatar2 from '~/components/Level/avatar2.png';
import avatar3 from '~/components/Level/avatar3.png';
import avatar4 from '~/components/Level/avatar4.png';
import { UseDosiminQueryOptions } from '~/components/Query/Query.hooks';

export type LevelMeta = {
  level: number;
  title: ReactNode;
  placeholderImage: string;
  headImage: string;
  name: string;
};

export type LevelUpRequirement = {
  level: number;
  nextLevel: number;
  minNftAmount: number;
  purchase: {
    currency: string;
    minAmount: number;
  };
  balance: {
    assetType: 'DON' | 'LUP' | 'UNKNOWN';
    minAmount: number;
  };
  minLevelUpPassAmount: number;
};

// NOTE: Only for initial data
export const levelUpRequirementMap: Record<number, LevelUpRequirement> = {
  1: {
    level: 1,
    nextLevel: 2,
    minNftAmount: 5,
    purchase: {
      currency: 'USD',
      minAmount: 0,
    },
    balance: {
      assetType: 'DON',
      minAmount: 12000,
    },
    minLevelUpPassAmount: 0,
  },
  2: {
    level: 2,
    nextLevel: 3,
    minNftAmount: 10,
    purchase: {
      currency: 'USD',
      minAmount: 1000,
    },
    balance: {
      assetType: 'DON',
      minAmount: 60000,
    },
    minLevelUpPassAmount: 0,
  },
  3: {
    level: 3,
    nextLevel: 4,
    minNftAmount: 15,
    purchase: {
      currency: 'USD',
      minAmount: 10000,
    },
    balance: {
      assetType: 'DON',
      minAmount: 600000,
    },
    minLevelUpPassAmount: 0,
  },
};

export type Benefit = {
  amount: number;
  holdingNums: number;
};

export type CheckInReward = {
  level: number;
  benefitList: Benefit[];
};

// NOTE: Only for initial data
export const levelMetaMap: Record<number, LevelMeta> = {
  1: {
    level: 1,
    title: <T name="citizen_level_1" />,
    placeholderImage: avatar1,
    headImage: lv1,
    name: 'Traveler',
  },
  2: {
    level: 2,
    title: <T name="citizen_level_2" />,
    placeholderImage: avatar2,
    headImage: lv2,
    name: 'Visitor',
  },
  3: {
    level: 3,
    title: <T name="citizen_level_3" />,
    placeholderImage: avatar3,
    headImage: lv3,
    name: 'Resident',
  },
  4: {
    level: 4,
    title: <T name="citizen_level_4" />,
    placeholderImage: avatar4,
    headImage: lv4,
    name: 'Citizen',
  },
};

export type CheckInRewardResponse = {
  rewardList: CheckInReward[];
};

export const checkInRewardsUrl = '/api/citizen/v1/events/check-in/rewards';

export const transformCheckInRewards = (checkInRewards: CheckInReward[]) =>
  checkInRewards.reduce<Record<number, Benefit[]>>((prev, cur) => {
    const level = cur.level;
    prev[level] = cur.benefitList;
    return prev;
  }, {});

export const getCheckInRewards = async () => {
  const { data } = await axios.get<CheckInRewardResponse>(checkInRewardsUrl);
  return transformCheckInRewards(data.rewardList);
};

export const useCheckInRewards = (options?: UseDosiminQueryOptions<Record<number, Benefit[]>>) =>
  useQuery<Record<number, Benefit[]>>(['checkInRewards'], getCheckInRewards, {
    initialData: {
      1: [],
      2: [],
      3: [],
      4: [],
    },
    ...options,
  });

export const useCheckInReward = (
  level: number,
  options?: UseDosiminQueryOptions<Record<number, Benefit[]>>
) => {
  const { data, ...rest } = useCheckInRewards(options);
  return {
    ...rest,
    data: data[level] || [],
  };
};

export const levelUpRequirementsUrl = '/api/citizen/v1/level-up/criteria';

export type LevelUpRequirementResponse = {
  criteriaList: LevelUpRequirement[];
};

export const transformLevelUpRequirements = (levelUpRequirements: LevelUpRequirement[]) =>
  levelUpRequirements.reduce<{ [key: string]: LevelUpRequirement }>((prev, cur) => {
    const level = cur.level;
    prev[level] = cur;
    return prev;
  }, {});

export const getLevelUpRequirements = async () => {
  const { data } = await axios.get<LevelUpRequirementResponse>(levelUpRequirementsUrl);
  return transformLevelUpRequirements(data.criteriaList);
};

export const useLevelUpRequirements = (
  options?: UseDosiminQueryOptions<Record<number, LevelUpRequirement>>
) =>
  useQuery<Record<number, LevelUpRequirement>>(['levelUpRequirement'], getLevelUpRequirements, {
    ...options,
    initialData: levelUpRequirementMap,
  });

export const useLevelUpRequirement = (
  level: number,
  options?: UseDosiminQueryOptions<Record<number, LevelUpRequirement>>
) => {
  const { data, ...rest } = useLevelUpRequirements(options);
  return {
    ...rest,
    data: data[level],
  };
};
